import type { RightResult, RouterOutputs } from '../../../../types/TRPC';
import type { PublicationProgressState } from '../../../../utils/publicationWorkflow';
import { Button } from '../../../Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '../../../ui/dialog';

type PublicationState = RightResult<
  RouterOutputs['release']['getStoreListingPublication']
>;

export function PublishListingDialog({
  open,
  state,
  busy,
  progress,
  error,
  onViewRelease,
  hasChanges,
  walletReady,
  walletMessage,
  onConnectWallet,
  isSavingDraft = false,
  onConfirm,
  onClose,
}: {
  open: boolean;
  state: PublicationState | null;
  busy: boolean;
  progress: PublicationProgressState | null;
  error: string | null;
  onViewRelease: () => void;
  hasChanges: boolean;
  walletReady: boolean;
  walletMessage?: string;
  onConnectWallet?: () => void;
  isSavingDraft?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const blocked = Boolean(state?.reason);
  const title = error
    ? 'Publication paused'
    : isSavingDraft
      ? 'Saving draft'
      : busy
        ? 'Submitting listing update'
        : state?.reason === 'review'
          ? 'A newer release is in review'
          : state?.reason === 'unfinished'
            ? 'Finish your current release first'
            : state?.reason === 'awaitingPublication'
              ? 'Your approved release is not live yet'
              : state?.reason === 'unavailable'
                ? 'Could not confirm the current release'
                : state?.reason === 'noLiveRelease'
                  ? 'Publish your first release'
                  : 'Publish listing updates?';
  const description =
    state?.reason === 'unavailable'
      ? 'The Store’s current version could not be checked. Your edits are still here. Close this dialog and try again shortly, or save a draft.'
      : state?.reason === 'review'
        ? 'Wait for the latest release to finish review before submitting listing changes. You can save your changes as a draft in the meantime.'
        : state?.reason === 'unfinished'
          ? 'You have a release that has not been submitted. Finish or discard it before creating a listing update. Your listing edits can still be saved as a draft.'
          : state?.reason === 'awaitingPublication'
            ? 'Wait for the approved release to go live so your listing update uses the latest APK. You can keep saving drafts while you wait.'
            : state?.reason === 'noLiveRelease'
              ? 'Listing updates reuse the APK currently in the Store. Save your draft, then submit your first APK from Update App.'
              : 'This creates a new release using the same APK as your current Store version. You’ll mint a Release NFT and submit it for review. Your changes reach the Store after review and publication.';
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => !next && !busy && !isSavingDraft && onClose()}
    >
      <DialogContent
        className="max-h-[calc(100dvh-2.5rem)] max-w-lg gap-6 overflow-y-auto rounded-xl border-seeker-800 bg-seeker-900 p-6 text-white [&>button:last-child]:hidden"
        onEscapeKeyDown={(event) =>
          (busy || isSavingDraft) && event.preventDefault()
        }
        onPointerDownOutside={(event) =>
          (busy || isSavingDraft) && event.preventDefault()
        }
      >
        <div className="space-y-2">
          <DialogTitle className="text-xl font-semibold leading-7">
            {title}
          </DialogTitle>
          <DialogDescription className="text-sm leading-6 text-seeker-400">
            {error
              ? 'Your listing changes are saved. Open the release to continue from the last completed step.'
              : description}
          </DialogDescription>
        </div>
        {!blocked && !error && state?.sourceRelease && (
          <dl className="divide-y divide-seeker-800 rounded-lg border border-seeker-800 px-4 text-sm">
            <div className="flex justify-between gap-4 py-3">
              <dt className="text-seeker-400">APK version</dt>
              <dd>
                {state.sourceRelease.versionName}{' '}
                <span className="text-seeker-400">· unchanged</span>
              </dd>
            </div>
            <div className="flex justify-between gap-4 py-3">
              <dt className="text-seeker-400">What changes</dt>
              <dd>Store listing</dd>
            </div>
          </dl>
        )}
        {busy && (
          <div role="status" aria-live="polite" className="space-y-3">
            <p className="text-sm text-seeker-300">
              {progress?.message || 'Preparing your release…'}
            </p>
            <div className="h-1 overflow-hidden rounded-full bg-seeker-800">
              <div
                className="h-full bg-seeker-500 transition-[width]"
                style={{ width: `${progress?.progress || 5}%` }}
              />
            </div>
            <p className="text-xs text-seeker-400">
              Keep this window open and approve the requests in your wallet.
            </p>
          </div>
        )}
        {error && (
          <p role="alert" className="text-sm leading-6 text-orange-400">
            {error}
          </p>
        )}
        {!busy && !blocked && !error && (
          <div className="space-y-3">
            <p className="text-sm text-seeker-400">
              {walletMessage ||
                'Your app wallet will approve the mint and review submission. Network fees apply.'}
            </p>
            {!walletReady && onConnectWallet && (
              <Button variant="outlineWhite" onClick={onConnectWallet}>
                Choose app wallet
              </Button>
            )}
          </div>
        )}
        <div className="flex flex-wrap justify-end gap-3">
          <Button
            variant="outlineMuted"
            onClick={onClose}
            disabled={busy || isSavingDraft}
          >
            {blocked || error ? 'Close' : 'Cancel'}
          </Button>
          {blocked || error ? (
            <Button
              variant="primary"
              onClick={onViewRelease}
              disabled={isSavingDraft}
              submitting={isSavingDraft}
            >
              {hasChanges ? 'Save draft & view release' : 'View release'}
              {state?.reason === 'noLiveRelease' ? 's' : ''}
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={onConfirm}
              disabled={!state || busy || !walletReady}
              submitting={busy}
            >
              Continue to mint
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
